import { FC, memo, useCallback } from "react";
import styled from "styled-components";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronLeft } from "@fortawesome/free-solid-svg-icons";
import { faChevronRight } from "@fortawesome/free-solid-svg-icons";
import Timer from "../_shared/Timer";
import { IWorkItem } from "../../../models/BlenderAgentStateModel";
import { getMediaColor, getMediaIcon } from "../../../style/theme";

interface IProps {
  workItem: IWorkItem;
  customerName: string;
  selectedWorkItemId: string;
  isTaskListCollapsed: boolean;
  unreadMessageCount: number;
  workItemClicked: (workItemId: string) => void;
  openTaskManagerClicked: () => void;
  isTaskManagerOpen: boolean;
}

const SelectorBar = styled.div<{ selected: boolean; color: string }>`
  width: ${(props) => (props.selected ? "10px" : "7px")};
  height: 100%;
  left: 0;
  position: absolute;
  background-color: ${(props) => props.color};
`;

const RootContainer = styled.div<{
  color: string;
  isSelected: boolean;
  isTaskListCollapsed: boolean;
}>`
  width: 100%;
  height: ${(props) => (props.isTaskListCollapsed ? "3rem" : "6rem")};
  display: flex;
  position: relative;
  cursor: pointer;
  border-bottom: 1px solid ${(props) => props.theme.default};
  background: ${(props) =>
    props.isSelected
      ? props.theme.highlightedTask
      : props.theme.primary};
  &:hover ${SelectorBar} {
    width: 10px;
  }
  transition: all 0.3s ease-out;
`;

const WorkItemLayout = styled.div`
  width: 100%;
  display: flex;
  padding-left: 1.5rem;
  padding-top: 0.5rem;
  padding-bottom: 0.5rem;
`;

const WorkItemDataContainer = styled.div`
  width: 80%;
  height: 100%;
  overflow: hidden;
`;

const IconContainer = styled.div<{
  color: string;
  isTaskListCollapsed: boolean;
}>`
  height: ${(props) => (props.isTaskListCollapsed ? "100%" : "40%")};
  display: flex;
  align-items: ${(props) =>
    props.isTaskListCollapsed ? "center" : "flex-end"};
  color: ${(props) => props.color};
`;

const WorkItemIcon = styled.div`
  position: relative;
  display: flex;
  align-items: flex-end;
`;

const IconSize = "1.3rem";

const TaskManagerContainer = styled.div`
  width: 20%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const WorkItemName = styled.div`
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const TimerContainer = styled.div`
  font-size: 0.9rem;
  color: ${(props) => props.theme.secondaryText};
`;

const UnreadMessageCount = styled.div`
  width: 0.8rem;
  height: 0.8rem;
  background-color: ${(props) => props.theme.alert};
  border-radius: 9999px;
  font-size: 0.5rem;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  position: absolute;
  top: 0;
  right: 0;
`;

const TaskListItem: FC<IProps> = memo(
  ({
    workItem,
    customerName,
    selectedWorkItemId,
    isTaskListCollapsed,
    unreadMessageCount,
    workItemClicked,
    openTaskManagerClicked,
    isTaskManagerOpen
  }) => {
    const isSelected = workItem.workItemId === selectedWorkItemId;
    const color = getMediaColor(workItem.primaryMediaType,workItem.primaryChannelType);
    const icon = getMediaIcon(workItem.primaryMediaType,workItem.primaryChannelType,isSelected);

    const handleWorkItemClicked = useCallback(() => {
      workItemClicked(workItem.workItemId);
    }, [workItem.workItemId, workItemClicked]);

    function handleTaskManagerClick(
      event: React.MouseEvent<HTMLInputElement>
    ): void {
      event.stopPropagation();
      openTaskManagerClicked();
    }

    return (
      <RootContainer
        isSelected={isSelected}
        color={color}
        isTaskListCollapsed={isTaskListCollapsed}
        onClick={handleWorkItemClicked}
      >
        <SelectorBar selected={isSelected} color={color} />
        <WorkItemLayout>
          <WorkItemDataContainer>
            {!isTaskListCollapsed && (
              <div>
                <WorkItemName>
                  {workItem.workItemState === "LookUp"
                    ? "New outbound email"
                    : customerName ?? "Unknown customer"}
                </WorkItemName>
                <TimerContainer>
                  <Timer date={workItem.createdAt} />
                </TimerContainer>
              </div>
            )}
            <IconContainer
              color={color}
              isTaskListCollapsed={isTaskListCollapsed}
            >
              <WorkItemIcon>
                <FontAwesomeIcon
                  icon={icon}
                  color={color}
                  style={{
                    width: IconSize,
                    height: IconSize,
                    paddingRight: "0.5rem",
                    paddingTop: "0.2rem",
                  }}
                />
                {unreadMessageCount > 0 && (
                  <UnreadMessageCount>{unreadMessageCount}</UnreadMessageCount>
                )}
              </WorkItemIcon>
            </IconContainer>
          </WorkItemDataContainer>
          {!isTaskListCollapsed && (
            <TaskManagerContainer
              onClick={handleTaskManagerClick}
              hidden={workItem.workItemState === "LookUp"}
            >
              <FontAwesomeIcon
                icon={isTaskManagerOpen ? faChevronLeft : faChevronRight}
                color={color}
                style={{ width: IconSize, height: IconSize }}
              />
            </TaskManagerContainer>
          )}
        </WorkItemLayout>
      </RootContainer>
    );
  }
);

export default TaskListItem;
